import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getTierById, getAccessForTier } from '../config/membershipConfig';
import { getToken, setToken as storeToken, clearToken, migrateLegacyTokens, authFetch, isTokenExpired, watchAccountDataIsolation } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EducationAuthContext = createContext(null);

export const EducationAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => {
    // Run migration on first load
    migrateLegacyTokens();
    return getToken();
  });

  // Fetch user profile
  const fetchUser = useCallback(async () => {
    const currentToken = getToken();
    if (!currentToken) {
      setUser(null);
      setLoading(false);
      return;
    }

    // P1 Fix 3 — Proactive expiry check. If the JWT is already expired
    // locally, clear it before making a round-trip and null out user state
    // so protected routes redirect to /education/login immediately.
    if (isTokenExpired(currentToken)) {
      clearToken();
      setTokenState(null);
      setUser(null);
      setLoading(false);
      // Dispatch the same global event as the authFetch 401 branch so
      // EducationAuthWatcher can navigate to /education/login with a proper
      // state.from return-path. Without this the watcher is bypassed and any
      // competing route-level <Navigate> guard wins the race, stripping from.
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(
            new CustomEvent('shs:auth-expired', {
              detail: {
                from:
                  typeof window.location !== 'undefined'
                    ? window.location.pathname + window.location.search
                    : '/',
                reason: 'expired',
              },
            })
          );
        } catch (_e) { /* silent — event dispatch is best-effort */ }
      }
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/education/me`);

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token invalid, clear it
        clearToken();
        setTokenState(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // ITER177 · If we're processing a Google OAuth callback, defer the /me
    // check — the callback route will call loginWithToken and set state
    // authoritatively. Reading /me first with no session yet would flash
    // an unauthenticated state.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  useEffect(() => watchAccountDataIsolation(), []);

  // P1 Fix 3 — Listen for global auth-expired events dispatched by authFetch
  // when a protected API call returns 401. Nulls out user + token state so
  // downstream `isAuthenticated`-gated pages redirect to /education/login.
  useEffect(() => {
    const handleAuthExpired = () => {
      setTokenState(null);
      setUser(null);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('shs:auth-expired', handleAuthExpired);
      return () => window.removeEventListener('shs:auth-expired', handleAuthExpired);
    }
    return undefined;
  }, []);

  // Register - using XMLHttpRequest to avoid fetch interception issues
  const register = async (email, password, firstName, lastName, preferredLanguage = 'en') => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/education/register`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (xhr.status >= 200 && xhr.status < 300 && data.verification_required) {
            resolve({ success: true, verificationRequired: true, email: data.email, emailSent: data.email_sent });
          } else if (xhr.status >= 200 && xhr.status < 300) {
            storeToken(data.token);
            setTokenState(data.token);
            setUser(data.user);
            resolve({ success: true, user: data.user });
          } else {
            // Parse error messages from backend
            let errorMessage = 'Registration failed';
            if (data.detail) {
              if (typeof data.detail === 'string') {
                errorMessage = data.detail;
              } else if (Array.isArray(data.detail)) {
                // Pydantic validation errors
                const errors = data.detail.map(err => {
                  const field = err.loc?.slice(-1)[0] || 'field';
                  return `${field}: ${err.msg}`;
                });
                errorMessage = errors.join(', ');
              }
            }
            // Common error translations for user-friendly messages
            if (errorMessage.toLowerCase().includes('already exists') || errorMessage.toLowerCase().includes('already registered')) {
              errorMessage = 'This email is already registered. Please sign in or use a different email.';
            }
            resolve({ success: false, error: errorMessage });
          }
        } catch (parseError) {
          console.error('JSON parse error:', parseError, 'Response:', xhr.responseText);
          resolve({ success: false, error: 'Server returned invalid response. Please try again.' });
        }
      };
      
      xhr.onerror = function() {
        console.error('Registration network error');
        resolve({ success: false, error: 'Unable to connect to server. Please check your internet connection.' });
      };
      
      xhr.ontimeout = function() {
        resolve({ success: false, error: 'Request timed out. Please try again.' });
      };
      
      xhr.timeout = 30000; // 30 second timeout
      
      xhr.send(JSON.stringify({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        preferred_language: preferredLanguage,
        origin_url: window.location.origin,
      }));
    });
  };

  // Login - Using XMLHttpRequest to avoid service worker body stream issues
  const login = async (email, password) => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/education/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          try {
            const data = JSON.parse(xhr.responseText);
            
            if (xhr.status === 200) {
              storeToken(data.token);
              setTokenState(data.token);
              setUser(data.user);
              
              // Sync UI language to user profile (non-blocking)
              const currentLang = localStorage.getItem('language') || 'en';
              if (data.user?.preferred_language !== currentLang) {
                try {
                  const syncXhr = new XMLHttpRequest();
                  syncXhr.open('PUT', `${API_URL}/api/education/me`, true);
                  syncXhr.setRequestHeader('Content-Type', 'application/json');
                  syncXhr.setRequestHeader('Authorization', `Bearer ${data.token}`);
                  syncXhr.send(JSON.stringify({ preferred_language: currentLang }));
                } catch (e) { /* silent */ }
              }
              
              resolve({ success: true, user: data.user });
            } else {
              resolve({ success: false, error: data.detail || 'Invalid email or password' });
            }
          } catch (e) {
            console.error('Login parse error:', e);
            resolve({ success: false, error: 'Server returned an invalid response' });
          }
        }
      };
      
      xhr.onerror = function() {
        console.error('Login network error - XHR onerror fired');
        resolve({ success: false, error: 'Network error. Please try again or clear your browser cache if this persists.' });
      };
      
      xhr.timeout = 15000;
      xhr.ontimeout = function() {
        console.error('Login timeout');
        resolve({ success: false, error: 'Request timed out. Please check your connection and try again.' });
      };
      
      xhr.send(JSON.stringify({ email, password }));
    });
  };

  // ITER177 · Google (Emergent OAuth) — accept a pre-issued backend JWT + user
  // record and mount the session. Same downstream contract as `login`.
  const loginWithToken = (nextToken, nextUser) => {
    if (!nextToken || !nextUser) return { success: false, error: 'missing_token_or_user' };
    storeToken(nextToken);
    setTokenState(nextToken);
    setUser(nextUser);
    return { success: true, user: nextUser };
  };

  // Logout
  const logout = () => {
    clearToken();
    setTokenState(null);
    setUser(null);
  };

  // Update profile
  const updateProfile = async (updates) => {
    try {
      const response = await authFetch(`${API_URL}/api/education/me`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      } else {
        const data = await response.json();
        return { success: false, error: data.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Create checkout session
  const createCheckout = async (planId) => {
    // Deprecated legacy plans cannot be purchased (server also returns 410).
    if (['explorer', 'deep_sleep', 'master_mind', 'professional'].includes(planId)) {
      return { success: false, error: 'This legacy plan is no longer offered.' };
    }
    try {
      const response = await authFetch(`${API_URL}/api/education/checkout/session`, {
        method: 'POST',
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, url: data.url, sessionId: data.session_id };
      } else {
        return { success: false, error: data.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Check checkout status
  const checkCheckoutStatus = async (sessionId) => {
    try {
      const response = await authFetch(`${API_URL}/api/education/checkout/status/${sessionId}`);

      const data = await response.json();

      if (response.ok) {
        // Refresh user data if payment successful
        if (data.payment_status === 'paid') {
          await fetchUser();
        }
        return { success: true, ...data };
      } else {
        return { success: false, error: data.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/education/cancel-subscription`, {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok) {
        await fetchUser();
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Check feature access
  const hasAccess = (feature) => {
    if (!user) return false;
    // ITER179 · shs_admin bypass — admins always have every learner feature
    // available for QA/preview (matches the backend admin gate).
    if (user.role === 'shs_admin') return true;
    return user.access?.[feature] === true;
  };

  // Get effective tier info
  const getTier = () => {
    const tierId = user?.effective_tier || user?.tier || 'free';
    return getTierById(tierId);
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Check if user has active subscription
  const hasActiveSubscription = user?.subscription_status === 'active';

  // Check if user is admin
  const isAdmin = user?.role === 'shs_admin';

  // Check if user can access content (admin bypasses all gates)
  const canAccessContent = (requiredTier) => {
    if (isAdmin) return true;
    if (!user) return requiredTier === 'free';
    
    const tierLevels = {
      free: 0,
      explorer: 1,
      deep_sleep: 2,
      master_mind: 3,
      professional: 4,
    };
    
    const userLevel = tierLevels[user.effective_tier || user.tier || 'free'] || 0;
    const requiredLevel = tierLevels[requiredTier] || 0;
    
    return userLevel >= requiredLevel;
  };

  const value = {
    user,
    loading,
    token: getToken(),
    isAuthenticated,
    hasActiveSubscription,
    isAdmin,
    canAccessContent,
    register,
    login,
    loginWithToken,
    logout,
    updateProfile,
    createCheckout,
    checkCheckoutStatus,
    cancelSubscription,
    hasAccess,
    getTier,
    refreshUser: fetchUser,
  };

  return (
    <EducationAuthContext.Provider value={value}>
      {children}
    </EducationAuthContext.Provider>
  );
};

export const useEducationAuth = () => {
  const context = useContext(EducationAuthContext);
  if (!context) {
    throw new Error('useEducationAuth must be used within an EducationAuthProvider');
  }
  return context;
};

export default EducationAuthContext;
